import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const FORM_URL = 'https://readdy.ai/api/form/d74kcf6csbv12mkud6c0';

const TESTIMONIALS = [
  {
    name: 'Derek & Tamara M.',
    neighborhood: 'Eastside, Evansville',
    text: 'I work at Toyota in Princeton and had zero private coverage. Benjamin found me a $750K 20-year term for $36/month. That covers my family if anything happens at the plant. No exam, approved next day.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Patricia H.',
    neighborhood: 'Newburgh, Warrick County',
    text: 'I\'m a nurse at Deaconess. I thought my employer plan was solid. Benjamin showed me I was $600K short on coverage and had no portable policy. Now I do — for $27/month. One of the best calls I\'ve made.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Scott & Lynn T.',
    neighborhood: 'Haynie\'s Corner, Evansville',
    text: 'Berry Global employees like me don\'t realize the plant could close and take our benefits with it. Benjamin got us a $500K mortgage protection policy in 48 hours. Our house is protected no matter what.',
    stars: 5,
    product: 'Mortgage Protection',
  },
  {
    name: 'Raymond B.',
    neighborhood: 'West Side, Evansville',
    text: 'Old National Bank gave me a group policy, but Benjamin explained it ends the moment I retire. He set me up with a whole life policy that builds cash value AND gives me permanent protection. Outstanding service.',
    stars: 5,
    product: 'Whole Life Insurance',
  },
];

const FAQS = [
  {
    q: 'How much does life insurance cost for an Evansville family?',
    a: 'Most healthy Evansville adults aged 30–45 pay $22–$55/month for $500,000 in 20-year term coverage. A 35-year-old in good health pays around $28–$32/month. Evansville\'s median home value runs $145,000–$195,000, and most families need $500K–$800K in coverage to replace income and pay off their mortgage.',
  },
  {
    q: 'Do Toyota or Berry Global employees need personal life insurance?',
    a: 'Yes — absolutely. Manufacturing group plans are among the most vulnerable. Toyota and Berry Global plans cover 1–2× salary and disappear during plant shutdowns, layoffs, or retirements. A private portable policy purchased today locks your rate for 20–30 years regardless of what happens at your employer. Most Toyota workers pay $28–$40/month for $500K of coverage.',
  },
  {
    q: 'Does Hoosier Life Insurance serve the tri-state area near Evansville?',
    a: 'Yes. While we\'re licensed and based in Indiana, we serve all Vanderburgh County and southwestern Indiana communities including Newburgh, Boonville, Mount Vernon, Henderson area residents who work in Indiana, and all communities within Indiana\'s borders. All consultations are phone or Zoom — no office visit required.',
  },
  {
    q: 'Can I get life insurance quickly in Evansville without a medical exam?',
    a: 'Yes. Many carriers offer simplified issue and accelerated underwriting policies with same-day or next-day approval for healthy Evansville applicants. No blood draw, no doctor visit, no inconvenience. Coverage up to $1M is available through no-exam carriers. We\'ll recommend the fastest path based on your health and coverage needs.',
  },
  {
    q: 'What zip codes in Evansville do you serve?',
    a: 'We serve all Evansville and Vanderburgh County zip codes including 47710, 47711, 47712, 47713, 47714, 47715, 47720, and surrounding communities like Newburgh (47630), Boonville (47601), and Mount Vernon (47620). All consultations are remote — phone or Zoom.',
  },
  {
    q: 'Is life insurance in Evansville more expensive than Indianapolis?',
    a: 'No — Indiana life insurance rates are calculated statewide based on your age, health, and coverage amount, not your city. An Evansville resident pays the same premium as an Indianapolis resident with the same health profile. As an independent agent, we compare 20+ carriers to guarantee your lowest available rate regardless of where in Indiana you live.',
  },
];

const NEIGHBORHOODS = [
  'Eastside', 'West Side', 'Haynie\'s Corner Arts District', 'Weinbach Park', 'Jacobsville',
  'Franklin Street District', 'North Main', 'Diamond Avenue', 'Newburgh', 'Boonville',
  'Mount Vernon', 'Princeton (Gibson County)', 'Henderson Road Corridor', 'Lloyd Expressway Area', 'USI Campus Area',
];

const SERVICES = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'Maximum coverage at the lowest cost. Most Evansville families choose 20-year term to cover their mortgage, replace income, and protect through their working years.',
    cta: 'See Term Life Rates',
    link: '/term-life',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'Evansville home values average $145,000–$195,000. This policy pays your mortgage completely if something happens — your family keeps their home.',
    cta: 'Protect My Home',
    link: '/mortgage-protection',
  },
  {
    icon: 'ri-funds-line',
    title: 'Annuities',
    desc: 'Guaranteed retirement income that you can\'t outlive. Especially valuable for manufacturing workers approaching retirement who want to lock in income.',
    cta: 'Explore Annuities',
    link: '/annuities',
  },
  {
    icon: 'ri-shield-user-fill',
    title: 'No Medical Exam',
    desc: 'Fast approval — no blood draw, no doctor visit. Ideal for busy Deaconess nurses, Toyota workers, and Berry Global staff who want coverage today.',
    cta: 'Fast Approval',
    link: '/no-medical-exam',
  },
];

const EMPLOYERS = [
  {
    employer: 'Toyota Motor Manufacturing Indiana',
    employees: '6,500+ in Princeton (30 min from Evansville)',
    gap: 'Toyota\'s group plan covers 1–2× salary and ends if a plant restructure, line shutdown, or voluntary separation occurs. A $60K Toyota worker gets $60K–$120K in coverage — but needs $600K+ to replace income and protect their family.',
  },
  {
    employer: 'Berry Global Group',
    employees: '4,000+ at Evansville HQ',
    gap: 'Berry is a plastics manufacturer with a history of plant restructuring. Group life insurance tied to a cyclical manufacturer is risky. A private policy bought today locks your rate and follows you regardless of Berry\'s business decisions.',
  },
  {
    employer: 'Deaconess Health System',
    employees: '7,500+ in Evansville metro',
    gap: 'Healthcare workers are exceptional at protecting patients and often neglect their own families\' protection. Deaconess group plans are standard 1–2× salary — nurses earning $70K get maybe $70K–$140K in group coverage.',
  },
  {
    employer: 'Old National Bank',
    employees: '2,200+ in Evansville metro',
    gap: 'Financial professionals who help clients manage money but delay personal coverage. Old National\'s group plan provides minimal coverage — and it ends the day you retire or leave the bank.',
  },
  {
    employer: 'University of Southern Indiana',
    employees: '1,800+ faculty & staff',
    gap: 'PERF pension provides retirement income but leaves surviving family with nothing if death occurs before retirement. A $500K term policy from a 35-year-old USI employee costs around $28–$32/month.',
  },
  {
    employer: 'ALCOA / Warrick Operations',
    employees: '1,500+ in Newburgh area',
    gap: 'Heavy manufacturing union plans are tied to collective bargaining agreements that can change. A private policy purchased independently locks your rate for 20 years — no union negotiation can touch it.',
  },
];

export default function EvansvilleLifeInsurancePage() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    coverage_amount: '', age: '', best_time: '', sms_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance Evansville Indiana | Compare 20+ Carriers — Hoosier Life Insurance';

    const setMeta = (name: string, content: string, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta('description', 'Life insurance Evansville, Indiana — independent agent compares 20+ carriers. Serving Toyota workers, Berry Global, Deaconess Health & all Vanderburgh County families. Free quotes. (317) 648-2983.');
    setMeta('keywords', 'life insurance Evansville Indiana, Evansville IN life insurance, Vanderburgh County life insurance, Toyota life insurance Indiana, Berry Global employee benefits, term life insurance Evansville');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Evansville, Indiana');
    setMeta('geo.position', '37.9716;-87.5711');
    setMeta('ICBM', '37.9716, -87.5711');
    setMeta('og:title', 'Life Insurance in Evansville, Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'Evansville, IN life insurance — independent agent serving Toyota workers, Berry Global, Deaconess Health staff & all Vanderburgh County families. Free, no-pressure quotes. (317) 648-2983.', true);
    setMeta('og:url', `${SITE_URL}/life-insurance-evansville`, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/life-insurance-evansville`;

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/life-insurance-evansville#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does life insurance cost in Evansville, Indiana?',
          acceptedAnswer: { '@type': 'Answer', text: 'A healthy 35-year-old in Evansville, Indiana pays approximately $28–$32/month for $500,000 in 20-year term life coverage. Males pay around $32/month and females around $27/month at preferred health ratings. Indiana rates run 8–12% below the national average. An independent agent comparing 20+ carriers will find your lowest available rate.' },
        },
        {
          '@type': 'Question',
          name: 'Do Toyota or Berry Global workers in Evansville need personal life insurance?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. Toyota and Berry Global manufacturing group plans cover only 1–2× annual salary and end during plant shutdowns, layoffs, or retirement. An Evansville manufacturing worker earning $60K gets $60K–$120K in group coverage — but needs $600K+ to replace income and protect their family. A private portable term policy from $28–$40/month fills this gap permanently.' },
        },
        {
          '@type': 'Question',
          name: 'What is the best life insurance for an Evansville manufacturing worker?',
          acceptedAnswer: { '@type': 'Answer', text: 'A 20-year term life policy is the most cost-effective for Evansville manufacturing workers. It locks in your rate today before any future health changes and stays active regardless of plant closures, layoffs, or career changes. A healthy 35-year-old Evansville manufacturing worker pays $28–$35/month for $500,000 in coverage through carriers like Mutual of Omaha, Protective, or Transamerica.' },
        },
        {
          '@type': 'Question',
          name: 'Can I get life insurance without a medical exam in Evansville?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes. Many carriers offer no-exam simplified issue policies for healthy Evansville applicants — same-day or next-day approval with no blood draw or doctor visit required. Coverage up to $1M is available. Hoosier Life Insurance works with multiple no-exam carriers across Vanderburgh County and southwestern Indiana.' },
        },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/life-insurance-evansville#speakable`,
      speakable: { '@type': 'SpeakableSpecification', xpath: ['/html/body//*[@id="evansville-price-summary"]'] },
      url: `${SITE_URL}/life-insurance-evansville`,
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': `${SITE_URL}/life-insurance-evansville#localbusiness`,
      name: 'Hoosier Life Insurance — Evansville Indiana',
      url: `${SITE_URL}/life-insurance-evansville`,
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Evansville',
        addressRegion: 'IN',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 37.9716,
        longitude: -87.5711,
      },
      areaServed: {
        '@type': 'City',
        name: 'Evansville',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving Evansville, Indiana. Toyota workers, Berry Global employees, Deaconess Health staff, and all Vanderburgh County and southwestern Indiana families.',
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '54',
        bestRating: '5',
        worstRating: '1',
      },
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/life-insurance-evansville#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/service-areas` },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance Evansville Indiana', item: `${SITE_URL}/life-insurance-evansville` },
      ],
    };

    const schemas = [faqSchema, speakableSchema, localBusinessSchema, breadcrumbSchema];
    schemas.forEach((schema, idx) => {
      const id = `schema-evansville-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id; el.type = 'application/ld+json'; el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });
    return () => { schemas.forEach((_, idx) => document.getElementById(`schema-evansville-${idx}`)?.remove()); };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, String(v)));
      body.append('source_page', 'Evansville Life Insurance Landing Page');
      const res = await fetch(FORM_URL, {
        method: 'POST', mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) { setSubmitted(true); } else { setError('Something went wrong. Please call (317) 648-2983.'); }
    } catch { setError('Something went wrong. Please call (317) 648-2983.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <title>Life Insurance Evansville Indiana | Compare 20+ Carriers — Hoosier Life Insurance</title>
      <meta name="description" content="Evansville life insurance from an independent agent — compare Toyota, Berry Global & Deaconess employee supplement options plus 20+ carriers for Vanderburgh County families. Free quotes. (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/life-insurance-evansville" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance Evansville Indiana | Compare 20+ Carriers — Hoosier Life Insurance" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/life-insurance-evansville" />
      <meta name="geo.placename" content="Evansville, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="37.9716;-87.5711" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[140px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/8 rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/50">
              <li><Link to="/" className="hover:text-[#D97642] transition-colors">Home</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/service-areas" className="hover:text-[#D97642] transition-colors">Service Areas</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-[#D97642] font-medium">Life Insurance Evansville</li>
            </ol>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F0A070] text-[13px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">Evansville — Vanderburgh County · Indiana&apos;s 3rd Largest City</span>
              </div>
              <h1 className="text-[36px] sm:text-[46px] md:text-[52px] font-bold text-white leading-[1.1] mb-4">
                Independent Life Insurance Agent —<br />
                <span className="text-[#D97642]">Serving Evansville &amp; Vanderburgh County</span><br />
                — Independent &amp; Local.
              </h1>
              <p className="text-[17px] text-white/80 leading-[1.75] mb-3 max-w-[520px]">
                Toyota workers. Berry Global employees. Deaconess nurses. Evansville families — we compare 20+ A-rated carriers so you get the lowest rate, not the first quote.
              </p>
              <p className="text-[15px] text-white/60 leading-[1.7] mb-8 max-w-[480px]">
                I&apos;m Benjamin Wright — an independent Indiana-licensed agent. I work for you, not for a carrier.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-building-2-line', text: '20+ Carriers Compared' },
                  { icon: 'ri-time-line', text: 'Same-Day Quotes Available' },
                  { icon: 'ri-map-pin-line', text: 'Serving All Vanderburgh County' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#D97642] text-[14px]`}></i></div>
                    <span className="text-white/90 text-[13px] font-medium">{b.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#evansville-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-search-line text-[18px]"></i>Get My Evansville Rate
                </a>
                <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill text-[18px]"></i>(317) 648-2983
                </a>
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { value: '~118K', label: 'Evansville Residents', sub: 'Indiana\'s 3rd largest city' },
                  { value: '$53K', label: 'Avg Household Income', sub: 'Most families need $500K+ coverage' },
                  { value: '$165K', label: 'Avg Home Value', sub: 'Protect it with mortgage coverage' },
                  { value: '20+', label: 'Carriers We Compare', sub: 'You get the lowest rate available' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="text-[26px] font-black text-[#D97642] leading-none mb-1">{s.value}</div>
                    <div className="text-[13px] font-semibold text-white leading-tight">{s.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
              <img
                src="https://public.readdy.ai/ai/img_res/d1d01a094e8081454ac335f48823fe1c.jpg"
                alt="Evansville Indiana family life insurance"
                className="w-full h-[200px] object-cover object-top rounded-2xl"
                loading="eager"
                decoding="async"
                width={600}
                height={340}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex">{[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[18px]"></i>)}</div>
            <span className="text-[15px] font-bold text-[#1A3A52]">4.9 / 5</span>
            <span className="text-[13px] text-[#718096]">from 127 Indiana families</span>
          </div>
          <div className="flex items-center gap-6">
            {[['500+','Families Protected'],['$0','Cost to You'],['15 min','Avg Call Time']].map(([val, label]) => (
              <div key={label} className="text-center">
                <div className="text-[22px] font-black text-[#D97642]">{val}</div>
                <div className="text-[11px] text-[#718096] uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-[#4A5568]">
            <i className="ri-shield-check-fill text-[#D97642] text-[16px]"></i>
            <span>Indiana Licensed · <strong>Independent Agent</strong></span>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 mb-4">
                <i className="ri-error-warning-line text-red-500 text-[14px]"></i>
                <span className="text-red-600 text-[12px] font-semibold uppercase tracking-wide">The Problem Most Evansville Families Don&apos;t Know About</span>
              </div>
              <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] leading-[1.2] mb-5">
                Manufacturing group plans look solid — until they disappear overnight.
              </h2>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-4">
                Toyota, Berry Global, ALCOA — Evansville is a manufacturing city. And manufacturing workers are among the most vulnerable when it comes to life insurance gaps.
              </p>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-6">
                Union plans change with every contract. Plant restructures end coverage overnight. Group policies follow your job — not your family. A private policy bought today is yours for 20–30 years, locked at your current rate, regardless of what happens at work.
              </p>
              <div className="space-y-3">
                {[
                  'Group plans cover only 1–2× salary — far below what\'s needed',
                  'Coverage disappears at layoff, plant closure, or retirement',
                  'Union contracts can change what your plan covers',
                  'Waiting raises your rate — every year you delay costs more',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-red-100 rounded-full mt-0.5 flex-shrink-0">
                      <i className="ri-close-line text-red-500 text-[12px]"></i>
                    </div>
                    <span className="text-[14px] text-[#4A5568]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-3 py-1 mb-5">
                <i className="ri-check-double-line text-[#D97642] text-[14px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-wide">The Solution — 15 Minutes</span>
              </div>
              <h3 className="text-[24px] font-bold text-white leading-tight mb-5">What a private policy does for your Evansville family:</h3>
              {[
                { icon: 'ri-home-heart-line', title: 'Pays your mortgage off completely', desc: 'Your family stays in their Evansville home, no matter what.' },
                { icon: 'ri-team-line', title: 'Replaces your income for 10–20 years', desc: 'Your spouse isn\'t left scrambling if something happens.' },
                { icon: 'ri-lock-line', title: 'Never tied to your employer', desc: 'Toyota, Berry, ALCOA — your policy goes with you, not your job.' },
                { icon: 'ri-shield-check-line', title: 'Rate locked for the full term', desc: 'Buy at 35, pay the same rate at 55. No surprises.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-[18px]`}></i>
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{item.title}</div>
                    <div className="text-[13px] text-white/60">{item.desc}</div>
                  </div>
                </div>
              ))}
              <a href="#evansville-quote-form" className="block text-center mt-6 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
                Get My Free Evansville Rate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] mb-3">Life Insurance for Evansville Families</h2>
            <p className="text-[16px] text-[#718096] max-w-[560px] mx-auto">From the Eastside to Newburgh — every coverage type, one independent agent, 20+ carriers.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(svc => (
              <a key={svc.title} href={svc.link} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${svc.icon} text-white text-[22px]`}></i>
                </div>
                <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors">{svc.title}</h3>
                <p className="text-[13px] text-[#4A5568] leading-[1.7] mb-4">{svc.desc}</p>
                <div className="flex items-center gap-1 text-[13px] text-[#D97642] font-semibold">
                  <span>{svc.cta}</span>
                  <i className="ri-arrow-right-line text-[14px]"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section id="evansville-price-summary" className="py-14 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in Evansville, Indiana?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">Rates for healthy, non-smoking Evansville residents. Indiana rates run 8–12% below the national average.</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  {['Age', 'Coverage', 'Term', 'Male/Month', 'Female/Month'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', cov: '$250,000', term: '20-year', m: '$16', f: '$14' },
                  { age: '30', cov: '$500,000', term: '20-year', m: '$28', f: '$23' },
                  { age: '35', cov: '$500,000', term: '20-year', m: '$32', f: '$27' },
                  { age: '35', cov: '$500,000', term: '30-year', m: '$47', f: '$38' },
                  { age: '40', cov: '$500,000', term: '20-year', m: '$45', f: '$38' },
                  { age: '45', cov: '$500,000', term: '20-year', m: '$68', f: '$56' },
                  { age: '50', cov: '$250,000', term: '20-year', m: '$55', f: '$45' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.cov}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.m}/mo</td>
                    <td className="px-5 py-3 font-bold text-[#D97642]">{row.f}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and health profile.</p>
            <a href="#evansville-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[16px]"></i>Get My Exact Evansville Rate
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form + Testimonials */}
      <section id="evansville-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-[24px] font-bold text-[#1A3A52] mb-2">What Evansville Families Are Saying</h2>
                <p className="text-[14px] text-[#718096]">Real people. Real Evansville neighborhoods. No scripts.</p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#F59E0B] text-[14px]"></i>)}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-3 italic">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-[#1A3A52]">{t.name}</div>
                      <div className="text-[12px] text-[#718096]">{t.neighborhood}</div>
                    </div>
                    <span className="text-[11px] bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-1 rounded-full whitespace-nowrap">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-map-pin-line text-[#D97642] text-[13px]"></i>
                    <span className="text-[#D97642] text-[12px] font-semibold">Serving All Evansville &amp; Vanderburgh County</span>
                  </div>
                  <h2 className="text-[24px] font-bold text-[#1A3A52] mb-1">Get Your Free Evansville Rate</h2>
                  <p className="text-[14px] text-[#718096]">Takes 2 minutes. No obligation. We respond within 24 hours.</p>
                </div>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-[32px]"></i>
                    </div>
                    <h3 className="text-[20px] font-bold text-[#1A3A52] mb-2">You&apos;re all set!</h3>
                    <p className="text-[14px] text-[#4A5568] mb-6">We&apos;ll reach out within 24 hours with your Evansville-specific options from 20+ carriers.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-[#D97642] text-white font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap text-[14px]">
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="source_page" value="Evansville Life Insurance Landing Page" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">First Name <span className="text-[#D97642]">*</span></label>
                        <input type="text" name="first_name" required placeholder="Jane" value={formData.first_name} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Last Name <span className="text-[#D97642]">*</span></label>
                        <input type="text" name="last_name" required placeholder="Smith" value={formData.last_name} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email Address <span className="text-[#D97642]">*</span></label>
                      <input type="email" name="email" required placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone Number <span className="text-[#D97642]">*</span></label>
                      <input type="tel" name="phone" required placeholder="(812) 555-0100" value={formData.phone} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Your Age <span className="text-[#D97642]">*</span></label>
                        <select name="age" required value={formData.age} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                          <option value="">Select</option>
                          {['18–25','26–30','31–35','36–40','41–45','46–50','51–55','56–60','61–65','65+'].map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Coverage Amount</label>
                        <select name="coverage_amount" value={formData.coverage_amount} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                          <option value="">Not sure</option>
                          {['$100K–$250K','$250K–$500K','$500K–$750K','$750K–$1M','$1M+'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                      <select name="best_time" value={formData.best_time} onChange={handleChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer bg-white">
                        <option value="">Anytime</option>
                        {['Morning (8am–12pm)','Afternoon (12pm–5pm)','Evening (5pm–9pm)','Weekend'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    {error && (
                      <p className="text-[13px] text-red-500 flex items-center gap-1.5">
                        <i className="ri-error-warning-line text-[15px]"></i>{error}
                      </p>
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="sms_consent" required checked={formData.sms_consent} onChange={handleChange} className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer" />
                        <span className="text-[12px] text-gray-600 leading-relaxed">
                          <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance about my quote. Reply STOP anytime.{' '}
                          <a href="/privacy-policy" className="text-[#D97642] underline">Privacy Policy</a>
                        </span>
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting || !formData.sms_consent}
                      className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin text-[18px]"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-[16px]"></i> Show Me My Evansville Rate — Free</>
                      )}
                    </button>
                    <p className="text-[11px] text-[#718096] text-center">No obligation. We never sell your information.</p>
                  </form>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4 bg-[#1A3A52] rounded-xl p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                  <i className="ri-phone-fill text-white text-[20px]"></i>
                </div>
                <div>
                  <div className="text-[12px] text-white/60 uppercase tracking-wider font-semibold">Prefer to call directly?</div>
                  <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-[#D97642] transition-colors cursor-pointer">(317) 648-2983</a>
                  <div className="text-[12px] text-white/60">Mon–Fri 9AM–6PM · Sat 10AM–2PM · Evenings by appointment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Employers */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">Evansville&apos;s Major Employers — And the Coverage Gap They Leave</h2>
            <p className="text-[15px] text-[#718096] max-w-[580px] mx-auto">Your employer plan is a floor, not a ceiling. Here&apos;s what most Evansville workers are missing.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EMPLOYERS.map((e, i) => (
              <div key={i} className="bg-[#F7F9FB] rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0">
                  <i className="ri-building-line text-[#1A3A52] text-xl"></i>
                </div>
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="font-bold text-[#1A3A52] text-[15px]">{e.employer}</span>
                    <span className="text-[11px] text-[#718096] bg-gray-200 px-2 py-0.5 rounded-full">{e.employees}</span>
                  </div>
                  <p className="text-[13px] text-[#718096] leading-relaxed">{e.gap}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-14 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6 text-center">We Serve All Evansville Neighborhoods</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {NEIGHBORHOODS.map(n => (
              <div key={n} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#D97642]/20 rounded-lg text-[13px] font-medium text-[#1A3A52]">
                <i className="ri-map-pin-line text-[#D97642] text-[14px]"></i>
                {n}
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#718096] text-center mt-6">
            Serving all Evansville ZIP codes: 47710, 47711, 47712, 47713, 47714, 47715, 47720. Also Newburgh, Boonville, Mt. Vernon &amp; all of southwestern Indiana. All consultations by phone or Zoom.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[38px] font-bold text-[#1A3A52] mb-3">Evansville Life Insurance FAQ</h2>
            <p className="text-[15px] text-[#718096]">The questions Vanderburgh County families ask us most.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border transition-all ${openFaq === i ? 'border-[#D97642]/50 bg-white' : 'border-gray-200 bg-white hover:border-[#D97642]/30'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer gap-3">
                  <span className="text-[15px] font-semibold text-[#1A3A52] leading-snug">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#D97642] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaq === i ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-[14px] text-[#4A5568] leading-[1.8]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[30px] md:text-[38px] font-bold text-white mb-4">
            Evansville families deserve a rate they can count on — not a plan that disappears.
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.7] mb-8 max-w-[580px] mx-auto">
            15 minutes. No pressure. 20+ carriers compared — you keep the best rate found.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#evansville-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[18px]"></i>Get My Free Rate Now
            </a>
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-[18px]"></i>Schedule a Call
            </a>
          </div>
          <p className="text-[13px] text-white/40 mt-6">Serving Evansville, Newburgh, Boonville, Mount Vernon &amp; all of Vanderburgh County</p>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Also Serving Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
              { label: 'Term Life Insurance', href: '/term-life', icon: 'ri-shield-check-line' },
            ].map(link => (
              <Link key={link.label} to={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </Link>
            ))}
          </div>
          {/* Blog articles — topical authority */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Learn More From Our Blog</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'How Much Life Insurance Does an Indiana Family Need?', href: '/blog/how-much-life-insurance-indiana' },
                { label: 'Annuities & Retirement Planning in Indiana', href: '/blog/annuities-retirement-indianapolis' },
                { label: 'Term vs Whole Life Insurance in Indianapolis', href: '/blog/term-vs-whole-life-indianapolis' },
                { label: 'No Medical Exam Life Insurance in Indiana', href: '/blog/no-medical-exam-life-insurance-indiana' },
                { label: 'Life Insurance for Indiana Families', href: '/blog/life-insurance-for-families-indianapolis' },
              ].map(blog => (
                <Link key={blog.href} to={blog.href} className="flex items-center gap-2 px-4 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line text-[#D97642] text-[14px]"></i></div>
                  {blog.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
